import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Suivi d'usage du Chat Live par page (statistiques marchand).
// Lit `chat_live_conversations` et calcule, par page d'origine :
//   • le nombre de conversations et de messages,
//   • le taux de réponse de l'assistant,
//   • les messages « non résolus » (réponse de repli / erreur).
// Les données sont strictement limitées à la boutique du marchand.
// ============================================================

interface ConvRow {
  id: number;
  session_id: string;
  role: string;
  content: string | null;
  page: string | null;
  created_at: string;
}

interface PageUsage {
  page: string;
  conversations: number;
  userMessages: number;
  assistantMessages: number;
  responseRate: number;
  unresolved: number;
}

/** Réponses de repli / erreur = message non résolu. */
function isUnresolved(content: string | null): boolean {
  if (!content) return false;
  const c = content.toLowerCase();
  return (
    c.includes("je n'ai pas pu répondre") ||
    c.includes('je suis désolé') ||
    c.includes('désolé, une erreur') ||
    c.includes('une erreur est survenue') ||
    c.includes("je n'ai pas")
  );
}

// (libellés de mois non nécessaires ici)

export default function ChatLiveUsage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ConvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState<7 | 30>(7);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error: err } = await supabase
        .from('chat_live_conversations')
        .select('id, session_id, role, content, page, created_at')
        .eq('idcommerce', getCommerceId(user))
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: true })
        .limit(5000);
      if (err) throw err;
      setRows((data as ConvRow[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, days]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const sessions = new Set(rows.map((r) => r.session_id)).size;
    const userMessages = rows.filter((r) => r.role === 'user').length;
    const assistantMessages = rows.filter((r) => r.role === 'assistant').length;
    const unresolved = rows.filter((r) => r.role === 'assistant' && isUnresolved(r.content)).length;
    const responseRate = userMessages > 0 ? Math.min(100, Math.round((assistantMessages / userMessages) * 100)) : 0;
    return { sessions, userMessages, assistantMessages, unresolved, responseRate };
  }, [rows]);

  const byPage = useMemo<PageUsage[]>(() => {
    const map = new Map<string, { sessions: Set<string>; user: number; assistant: number; unresolved: number }>();
    rows.forEach((r) => {
      const key = (r.page || 'inconnue').trim() || 'inconnue';
      const e = map.get(key) || { sessions: new Set<string>(), user: 0, assistant: 0, unresolved: 0 };
      e.sessions.add(r.session_id);
      if (r.role === 'user') e.user += 1;
      if (r.role === 'assistant') {
        e.assistant += 1;
        if (isUnresolved(r.content)) e.unresolved += 1;
      }
      map.set(key, e);
    });
    return Array.from(map.entries())
      .map(([page, e]) => ({
        page,
        conversations: e.sessions.size,
        userMessages: e.user,
        assistantMessages: e.assistant,
        responseRate: e.user > 0 ? Math.min(100, Math.round((e.assistant / e.user) * 100)) : 0,
        unresolved: e.unresolved,
      }))
      .sort((a, b) => b.userMessages - a.userMessages);
  }, [rows]);

  const prettyPage = (p: string) => {
    if (p === 'inconnue') return 'Page inconnue';
    try {
      if (p.startsWith('http')) {
        const u = new URL(p);
        return u.pathname === '/' ? 'Accueil' : u.pathname;
      }
    } catch { /* ignore */ }
    return p;
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-chat-smile-2-line text-primary-500"></i>
            Chat Live — usage par page
          </h3>
          <p className="text-xs text-foreground-400 mt-0.5">
            {summary.sessions} conversation(s) · {summary.userMessages} message(s) visiteur(s)
          </p>
        </div>
        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 self-start">
          {([7, 30] as const).map((p) => (
            <button
              key={p}
              onClick={() => setDays(p)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${days === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              {p} jours
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={load} className="ml-auto underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10">
          <i className="ri-chat-3-line text-3xl text-foreground-300 block mb-2"></i>
          <p className="text-sm text-foreground-400">Aucune conversation Chat Live sur la période</p>
        </div>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg bg-background-100/50 p-3">
              <p className="text-[11px] text-foreground-500">Taux de réponse</p>
              <p className="text-lg font-bold text-foreground-950">{summary.responseRate}%</p>
            </div>
            <div className="rounded-lg bg-background-100/50 p-3">
              <p className="text-[11px] text-foreground-500">Réponses assistant</p>
              <p className="text-lg font-bold text-foreground-950">{summary.assistantMessages}</p>
            </div>
            <div className="rounded-lg bg-background-100/50 p-3">
              <p className="text-[11px] text-foreground-500">Messages non résolus</p>
              <p className={`text-lg font-bold ${summary.unresolved > 0 ? 'text-red-500' : 'text-foreground-950'}`}>{summary.unresolved}</p>
            </div>
            <div className="rounded-lg bg-background-100/50 p-3">
              <p className="text-[11px] text-foreground-500">Pages avec chat</p>
              <p className="text-lg font-bold text-foreground-950">{byPage.length}</p>
            </div>
          </div>

          {/* Tableau par page */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left py-2 px-2 text-xs font-semibold text-foreground-500">Page</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-foreground-500">Conversations</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-foreground-500">Messages</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-foreground-500">Taux de réponse</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-foreground-500">Non résolus</th>
                </tr>
              </thead>
              <tbody>
                {byPage.map((p) => (
                  <tr key={p.page} className="border-b border-background-200/70 last:border-0">
                    <td className="py-2 px-2 text-xs text-foreground-800 truncate max-w-[220px]">{prettyPage(p.page)}</td>
                    <td className="py-2 px-2 text-right text-xs text-foreground-600">{p.conversations}</td>
                    <td className="py-2 px-2 text-right text-xs text-foreground-600">{p.userMessages}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`text-xs font-medium ${p.responseRate >= 80 ? 'text-accent-700' : p.responseRate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{p.responseRate}%</span>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <span className={`text-xs font-medium ${p.unresolved > 0 ? 'text-red-500' : 'text-foreground-500'}`}>{p.unresolved}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}